import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { io, Socket } from 'socket.io-client'
import { AuthService } from './auth.service'
import { ServerUrlService } from './server-url.service'

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private socket: Socket
  private destroy$ = new Subject<void>()
  private connected$ = new BehaviorSubject<boolean>(false)

  constructor(
    private readonly authService: AuthService,
    private readonly serverUrl: ServerUrlService
  ) {
    this.socket = io(this.serverUrl.url, {
      transports: ['websocket'],
      path: '/nest-ws',
      auth: {
        token: this.authService.token,
      },
    })

    this.socket.on('connect', () => this.connected$.next(true))
    this.socket.on('disconnect', () => this.connected$.next(false))
  }

  emit<T>(eventName: string, data: T): void {
    this.socket.emit(eventName, data)
  }

  emitWithCallback<T, R>(eventName: string, data: T): Observable<R> {
    return new Observable<R>((subscriber) => {
      let completed = false

      this.socket.emit(eventName, data, (response: R) => {
        if (completed) {
          return
        }
        completed = true
        subscriber.next(response)
        subscriber.complete()
      })

      return () => {
        completed = true
      }
    })
  }

  listen<T>(eventName: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      const handler = (data: T): void => subscriber.next(data)

      this.socket.on(eventName, handler)

      return () => {
        this.socket.off(eventName, handler)
      }
    })
  }

  isConnected(): Observable<boolean> {
    return this.connected$.asObservable()
  }

  onReconnect(callback: () => void): void {
    this.socket.on('connect', callback)
  }

  disconnect(): void {
    this.socket.disconnect()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.connected$.complete()
    this.disconnect()
  }
}
